import { useState, useCallback, useEffect } from "react";
import { listDrives } from "../../../../../services/drives";

/**
 * Hook para obtener la lista de drives (sin store).
 * Llama a listDrives cuando el modal abre; mantiene drives en memoria al cerrar para no refetch.
 * @param {boolean} isOpen - Si el modal está abierto
 * @returns {Object} { drives, isLoading, error, defaultVaultUuid }
 */
export const useDrivesList = (isOpen) => {
  const [drives, setDrives] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchDrives = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const resp = await listDrives({
        scope: "MANAGE",
        meta: {
          page: 1,
          page_size: 100,
          order_by: "created_at",
          order_direction: "DESC",
          filters: {},
        },
      });
      const d = resp?.data;
      let items = [];

      if (Array.isArray(d?.body)) items = d.body;
      else if (Array.isArray(d?.data)) items = d.data;
      else if (Array.isArray(d?.body?.data)) items = d.body.data;
      else if (Array.isArray(d?.items)) items = d.items;

      const mapped = items.map((item) => ({
        id: item.vaultUuid || item.vault_uuid || item.id || item.uuid,
        name: item.name || item.title || "Untitled Drive",
      }));
      setDrives(mapped);
    } catch (err) {
      setError(err?.message ?? "Failed to load drives");
      setDrives([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen && drives.length === 0) {
      fetchDrives();
    }
  }, [isOpen, fetchDrives, drives.length]);

  const defaultVaultUuid = drives.length > 0 ? drives[0].id : null;

  return {
    drives,
    isLoading,
    error,
    defaultVaultUuid,
  };
};
